// DD-3735: what reaches GlitchTip. Everything here runs in the SDK's beforeSend, so an event is
// dropped (null) or scrubbed before it leaves the browser:
// - noise that is not a bug in the UI: browser extensions, ResizeObserver warnings, aborted or
//   offline requests, ended sessions and gateway outages, chunk errors after a release (reload);
// - personal data: query strings and hashes are stripped from every URL, and personal identity
//   numbers are masked in messages, since both can carry patient details;
// - floods: each distinct error is sent once per page, and at most MAX_EVENTS_PER_PAGE in all.
import type { Breadcrumb, ErrorEvent, EventHint, StackFrame } from '@sentry/react';
import { isExpectedErrorMessage } from './expected-errors';
import { isChunkLoadError, reloadOnceForChunkError } from './chunk-reload';

export const MAX_EVENTS_PER_PAGE = 10;

const EXTENSION_URL = /^(chrome|moz|safari(-web)?|ms-browser)-extension:\/\//;

const NOISE_MESSAGES = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // Offline or dropped connections, worded per browser (Chrome, Firefox, Safari, React Native).
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Load failed',
    'Network request failed',
    // Aborted requests (navigation away, unmounted component).
    'The user aborted a request',
    'The operation was aborted',
    'signal is aborted without reason',
];

// Swedish personal identity numbers, with or without century and separator: 121212-1212,
// 19121212-1212, 1212121212.
const PERSONAL_NUMBER = /\b(?:\d{2})?\d{6}[-+]?\d{4}\b/g;
// Everything from the first ? or # of a URL to its end.
const URL_QUERY_OR_HASH = /(\b(?:https?|wss?):\/\/[^\s?#"'<>]*)[?#][^\s"'<>]*/g;

const sentFingerprints = new Set<string>();
let sentCount = 0;

/** Test helper: forget what this page has sent. */
export const resetSentEvents = (): void => {
    sentFingerprints.clear();
    sentCount = 0;
};

export const stripUrl = (url: string): string => url.split(/[?#]/)[0];

const scrubText = (text: string): string =>
    text.replace(URL_QUERY_OR_HASH, '$1').replace(PERSONAL_NUMBER, '[redacted]');

const getFrames = (event: ErrorEvent): StackFrame[] =>
    (event.exception?.values ?? []).flatMap((exception) => exception.stacktrace?.frames ?? []);

const getMessages = (event: ErrorEvent, hint?: EventHint): string[] => {
    const original = hint?.originalException as { message?: unknown } | undefined;
    return [
        event.message,
        typeof original?.message === 'string' ? original.message : undefined,
        typeof hint?.originalException === 'string' ? hint.originalException : undefined,
        ...(event.exception?.values ?? []).map((exception) => exception.value),
    ].filter((message): message is string => !!message);
};

const isOwnScript = (filename: string | undefined, origin: string): boolean =>
    !!filename && filename.startsWith(`${origin}/static/js/`);

/**
 * True when the error did not come from our own bundle: a frame from a browser extension, or a
 * stack where no frame is ours. "Script error." is what the browser reports for a cross-origin
 * script, with no stack to go on.
 */
export const isThirdPartyError = (event: ErrorEvent, hint: EventHint | undefined, origin: string): boolean => {
    const frames = getFrames(event);
    if (frames.some((frame) => EXTENSION_URL.test(frame.filename ?? ''))) return true;
    if (frames.length > 0 && !frames.some((frame) => isOwnScript(frame.filename, origin))) return true;
    return frames.length === 0 && getMessages(event, hint).some((message) => /^Script error\.?$/.test(message));
};

export const isNoiseError = (event: ErrorEvent, hint?: EventHint): boolean => {
    const original = hint?.originalException as { name?: unknown } | undefined;
    if (original?.name === 'AbortError') return true;
    if ((event.exception?.values ?? []).some((exception) => exception.type === 'AbortError')) return true;
    // Whatever fails while the browser is offline is a symptom of being offline.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    return getMessages(event, hint).some(
        (message) => isExpectedErrorMessage(message) || NOISE_MESSAGES.some((noise) => message.includes(noise)),
    );
};

// Apollo errors that reach the global handlers (a mutation promise nobody caught) are reported at
// the source instead: network errors by the Apollo error link, GraphQL errors by the backend.
const isUncaughtApolloError = (event: ErrorEvent, hint?: EventHint): boolean => {
    const original = hint?.originalException as { name?: unknown; graphQLErrors?: unknown } | undefined;
    const isApolloError = original?.name === 'ApolloError' || Array.isArray(original?.graphQLErrors);
    return isApolloError && event.tags?.source !== 'error-boundary' && event.tags?.source !== 'apollo-network';
};

const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
    const data = breadcrumb.data ? { ...breadcrumb.data } : undefined;
    if (data) {
        ['url', 'from', 'to'].forEach((key) => {
            if (typeof data[key] === 'string') data[key] = stripUrl(data[key]);
        });
    }
    return {
        ...breadcrumb,
        message: breadcrumb.message ? scrubText(breadcrumb.message) : breadcrumb.message,
        data,
    };
};

/** Removes query strings, hashes and personal identity numbers from the event, in place. */
export const scrubEvent = (event: ErrorEvent): ErrorEvent => {
    if (event.message) event.message = scrubText(event.message);
    if (event.request) {
        if (event.request.url) event.request.url = stripUrl(event.request.url);
        const headers = event.request.headers;
        if (headers?.Referer) headers.Referer = stripUrl(headers.Referer);
        delete event.request.query_string;
        delete event.request.cookies;
        delete event.request.data;
    }
    (event.exception?.values ?? []).forEach((exception) => {
        if (exception.value) exception.value = scrubText(exception.value);
        (exception.stacktrace?.frames ?? []).forEach((frame) => {
            if (frame.filename) frame.filename = stripUrl(frame.filename);
            if (frame.abs_path) frame.abs_path = stripUrl(frame.abs_path);
        });
    });
    if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
    return event;
};

/** Type, message and the innermost frame of our own code: two events with the same key are one error. */
const fingerprintOf = (event: ErrorEvent, origin: string): string => {
    const exception = event.exception?.values?.[event.exception.values.length - 1];
    const ownFrames = (exception?.stacktrace?.frames ?? []).filter((frame) => isOwnScript(frame.filename, origin));
    // Sentry orders frames outermost first, so the throwing frame is the last one.
    const topFrame = ownFrames[ownFrames.length - 1];
    return [
        exception?.type ?? '',
        exception?.value ?? event.message ?? '',
        topFrame ? `${topFrame.filename}:${topFrame.function}:${topFrame.lineno}:${topFrame.colno}` : '',
    ].join('|');
};

type BeforeSendOptions = {
    origin?: string;
    reload?: () => void;
};

export const createBeforeSend =
    ({ origin = window.location.origin, reload }: BeforeSendOptions = {}) =>
    (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
        const isChunkError =
            isChunkLoadError(hint?.originalException) ||
            (event.exception?.values ?? []).some((exception) =>
                isChunkLoadError({ name: exception.type, message: exception.value }),
            );
        if (isChunkError && reloadOnceForChunkError(reload)) return null;
        if (isNoiseError(event, hint)) return null;
        if (isThirdPartyError(event, hint, origin)) return null;
        if (isUncaughtApolloError(event, hint)) return null;

        scrubEvent(event);

        if (sentCount >= MAX_EVENTS_PER_PAGE) return null;
        const fingerprint = fingerprintOf(event, origin);
        if (sentFingerprints.has(fingerprint)) return null;
        sentFingerprints.add(fingerprint);
        sentCount += 1;
        return event;
    };
